import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import * as _ from 'lodash';
import * as moment from 'moment';
import { ShiftType } from '@enums/enum';
import { AvailabilityRange, ShiftSummaryList } from '../../entities/availability-range';
import { Driver } from '../../entities/driver';
import { DriverAvailability } from '../../entities/driver-availability';

export interface DriverScheduleChangeEvent {
    driverId: string;
    date: string;
    shiftType: ShiftType;
    warehouse: string;
}

const DAY_SLICES = [0, 10, 20, 30] as const;

@Component({
    selector: 'app-driver-monthly-schedule',
    templateUrl: './driver-monthly-schedule.component.html',
    styleUrls: ['./driver-monthly-schedule.component.scss']
})
export class DriverMonthlyScheduleComponent implements OnChanges {
    @Input() selectedDriver: Driver;
    @Input() monthSchedule: AvailabilityRange;
    @Input() daysInMonth: moment.Moment[];
    @Input() locale: string;
    @Input() warehouse: string;
    @Input() availabilityList: DriverAvailability[] = [];

    @Output() close = new EventEmitter<void>();
    @Output() change = new EventEmitter<DriverScheduleChangeEvent>();
    @Output() prevMonth = new EventEmitter<void>();
    @Output() nextMonth = new EventEmitter<void>();

    readonly daySlices = DAY_SLICES;

    private shiftSummaryByDate = new Map<string, ShiftSummaryList>();
    private availabilityByKey = new Map<string, DriverAvailability>();

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['monthSchedule']?.currentValue) {
            this.buildShiftSummaryMap();
        }
        if (changes['availabilityList']?.currentValue) {
            this.buildAvailabilityMap();
        }
    }

    private buildShiftSummaryMap(): void {
        this.shiftSummaryByDate.clear();
        const list = this.monthSchedule?.shiftSummaryList ?? [];
        list.forEach((s: ShiftSummaryList) => this.shiftSummaryByDate.set(s.date, s));
    }

    private buildAvailabilityMap(): void {
        this.availabilityByKey.clear();
        (this.availabilityList ?? []).forEach((a: DriverAvailability) => {
            this.availabilityByKey.set(`${a.driverId}|${a.date}|${a.shift}`, a);
        });
    }

    public findShifts(day: moment.Moment): ShiftSummaryList | undefined {
        const dateStr = moment.isMoment(day) ? day.format('YYYY-MM-DD') : moment(day).format('YYYY-MM-DD');
        return this.shiftSummaryByDate.get(dateStr);
    }

    public isAvailable(driverId: string, date: string, shiftType: string): DriverAvailability | undefined {
        return this.availabilityByKey.get(`${driverId}|${date}|${shiftType}`);
    }

    public onClose(): void {
        this.close.emit();
    }

    public onPrevMonth(): void {
        this.prevMonth.emit();
    }

    public onNextMonth(): void {
        this.nextMonth.emit();
    }

    public onChange(driverId: string, date: string, shiftType: string, warehouse: string): void {
        this.change.emit({ driverId, date, shiftType: shiftType as ShiftType, warehouse });
    }
}
